export interface ProductSearch {
  term: string
  count: number
  page: number
  region: string
  facet: string[]
  locale: string
  sort: string
  order: string
  store: string
}

export interface ResponseData {
  requestDate: number
  responseDate: number
  version: number
  term: string[]
  count: number
  reagents: Reagents[]
  controls: Controls[]
}

export interface DropDownResponse {
  physicalMaterial: string[]
  scale: string[]
  modification: string[]
  purification: string[]
  format: string[]
  species: string[]
  structure: string[]
  amount: string[]
  vector: string[]
  titer: string[]
  volume: string[]
  aliquot: string[]
  lentivirusBackbone: string[]
  companionProducts: string[]
}

export interface PriceCallResponse {
  result: PriceCallResult[]
}

export interface PriceCallResult {
  grnaId: string
  geneId: string
  geneSymbol: string
  specificityScore: string
  efficiencyScore: string
  species: string
  moreInformation: string
  recommendedStatus: string
  price: number
  currency: string
  brand: string
  material: string
  currencySymbol: string
  __typename?: string
}

export interface Reagents {
  grnaId: string
  geneId: string
  geneSymbol: string
  specificityScore: string
  efficiencyScore: string
  species: string
  moreInformation: string
  recommendedStatus: string
  price: number
  currency: string
  __typename: string
}

export interface Controls {
  grnaId: string
  geneId: string
  geneSymbol: string
  specificityScore: string
  efficiencyScore: string
  species: string
  moreInformation: string
  recommendedStatus: string
  price: number
  currency: string
  __typename: string
}

export interface DropDownSearch {
  countryCode: string
  physicalMaterial: string
  modification: string
  structure: string
  species: string
  titer: string
  volume: string
  scale: string
  purification: string
  store: string
}

export enum Species {
  human = 'Human',
  mouse = 'Mouse',
  Empty = '',
}

export interface Checkboxes {
  mouse: boolean
  human: boolean
}

export interface ReagentsItem {
  currency: string
  efficiencyScore: string
  geneId: string
  geneSymbol: string
  grnaId: string
  moreInformation: string
  price: number
  recommendedStatus: string
  species: string
  specificityScore: string
  __typename?: string
}

export interface PhysicalFormatItem {
  physicalMaterial: string
  scale: string
  modification: string
  purification: string
  format: string
  species: string
  structure: string
  amount: string
  vector: string
  titer: string
  volume: string
  aliquot: string
  lentivirusBackbone: string
}

export interface PriceCallData {
  reagents: ReagentsItem[]
  country: string
  physicalFormat: PhysicalFormatItem
}

export interface SpecificationValues {
  physicalMaterial: string
  scale: string
  modification: string
  purification: string
  species: string
  structure: string
  amount: string
  vector: string
  titer: string
  volume: string
  aliquot: string
  lentivirusBackbone: string
}

export interface PaginationData {
  totalCount: number
  page: number
  controlItemsCount: number
  perPageCount: number
}

export interface CheckLinks {
  itemSelectionLink: boolean
  specificationLink: boolean
}

export interface CrisprgrnaAddtoCartData {
  priceCallData: PriceCallData
  sapNextEnabled: boolean
  locale: string
  timeZone: string
  store: string
}

export interface SearchState {
  searchValue: ProductSearch
  searchResponseJson: ResponseData
  dropDownResponseList: DropDownResponse
  priceCallResponse: PriceCallResponse
  checkboxes: Checkboxes
  dropDownValue: DropDownSearch
  priceCallData: PriceCallData
  specificationValues: SpecificationValues
  paginationData: PaginationData
  stepNo: number
  traceOnClick: boolean
  checkFileUpload: boolean
  checkActiveLink: CheckLinks
  pageError: string
  crisprgrnaAddtoCartData: CrisprgrnaAddtoCartData
  setIsAllChecked: boolean
  setPage: number
  perPageCount: string
  companionProducts?: any[] | null | undefined
}

export const initialState: SearchState = {
  searchResponseJson: {
    requestDate: 0,
    responseDate: 0,
    version: 0,
    term: [],
    count: 0,
    reagents: [],
    controls: [],
  },
  dropDownResponseList: {
    physicalMaterial: [],
    scale: [],
    modification: [],
    purification: [],
    format: [],
    species: [],
    structure: [],
    amount: [],
    vector: [],
    titer: [],
    volume: [],
    aliquot: [],
    lentivirusBackbone: [],
    companionProducts: [],
  },
  priceCallResponse: {
    result: [],
  },
  searchValue: {
    term: '',
    count: 10,
    page: 1,
    region: '',
    facet: ['species:Human', 'species:Mouse'],
    locale: '',
    sort: 'grnaId',
    order: 'ASC',
    store: '',
  },
  checkboxes: {
    human: false,
    mouse: false,
  },
  dropDownValue: {
    countryCode: '',
    physicalMaterial: '',
    modification: '',
    structure: '',
    species: '',
    titer: '',
    volume: '',
    scale: '',
    purification: '',
    store: '',
  },
  priceCallData: {
    reagents: [],
    country: '',
    physicalFormat: {
      physicalMaterial: '',
      scale: '',
      modification: '',
      purification: '',
      format: '',
      species: 'SpCas9',
      structure: '',
      amount: '1',
      vector: 'LV01',
      titer: '10^6',
      volume: '0.2',
      aliquot: '1 x 200',
      lentivirusBackbone: 'LV01',
    },
  },
  specificationValues: {
    physicalMaterial: '',
    scale: '',
    modification: '',
    purification: '',
    species: 'SpCas9',
    structure: '',
    amount: '1 μg',
    vector:
      '(LV01) U6-gRNA:ef1a-puro-2A-Cas9-2A-tGFP (Sigma all-in-one lentiviral plasmid)',
    titer: '10^6 particles/ml',
    volume: '200 μl',
    aliquot: '1 x 200 μl',
    lentivirusBackbone:
      '(LV01) U6-gRNA:ef1a-puro-2A-Cas9-2A-tGFP (Sigma all-in-one lentivirus)',
  },
  paginationData: {
    totalCount: 0,
    page: 1,
    controlItemsCount: 0,
    perPageCount: 1,
  },
  stepNo: 0,
  traceOnClick: false,
  checkFileUpload: false,
  setIsAllChecked: false,
  setPage: 1,
  checkActiveLink: {
    itemSelectionLink: false,
    specificationLink: false,
  },
  pageError: '',
  perPageCount: '10',
  crisprgrnaAddtoCartData: {
    priceCallData: {
      reagents: [],
      country: '',
      physicalFormat: {
        physicalMaterial: '',
        scale: '',
        modification: '',
        purification: '',
        format: '',
        species: 'SpCas9',
        structure: '',
        amount: '1',
        vector: 'vector',
        titer: '10^6',
        volume: '0.2',
        aliquot: '1 x 200',
        lentivirusBackbone: 'LV01',
      },
    },
    sapNextEnabled: false,
    locale: '',
    timeZone: '',
    store: '',
  },
  companionProducts: [],
}

type Action =
  | { type: 'INIT' }
  | { type: 'UPDATE_SEARCH_RESPONSE'; data: any }
  | { type: 'UPDATE_DROPDOWN_RESPONSE'; data: any }
  | { type: 'UPDATE_PRICE_CALL_RESPONSE'; data: any }
  | { type: 'UPDATE_SEARCH_TERM'; data: ProductSearch }
  | { type: 'UPDATE_CHECK_BOX'; data: Checkboxes }
  | { type: 'UPDATE_DROP_DOWN'; data: DropDownSearch }
  | { type: 'UPDATE_PRICE_CALL'; data: PriceCallData }
  | { type: 'UPDATE_SPECIFICATION_VALUES'; data: SpecificationValues }
  | { type: 'UPDATE_PAGINATION_DATA'; data: PaginationData }
  | { type: 'SET_STEP_NO'; data: number }
  | { type: 'TRACE_ON_CLICK'; data: boolean }
  | { type: 'CHECK_FILE_UPLOAD'; data: boolean }
  | { type: 'SET_PAGE_ERROR'; data: string }
  | { type: 'UPDATE_ADD_TO_CART_DATA'; data: CrisprgrnaAddtoCartData }
  | { type: 'CHECK_ACTIVE_LINK'; data: CheckLinks }
  | { type: 'SET_INITIAL_STATE'; data: SearchState }
  | { type: 'SET_IS_ALL_CHECKED'; data: boolean }
  | { type: 'SET_PAGINATION_PAGE'; data: number }
  | { type: 'SET_PER_PAGE_COUNT'; data: string }
  | { type: 'SET_COMPANION_PRODUCT_INFORMATION'; data: any[] }

export interface ContextProps {
  state: SearchState
  dispatch: ({ type, data }) => void
}

export function reducer(state: SearchState, action: Action): SearchState {
  const keys = {
    UPDATE_SEARCH_RESPONSE: 'searchResponseJson',
    UPDATE_DROPDOWN_RESPONSE: 'dropDownResponseList',
    UPDATE_PRICE_CALL_RESPONSE: 'priceCallResponse',
    UPDATE_SEARCH_TERM: 'searchValue',
    UPDATE_SPECIFICATION_VALUES: 'specificationValues',
    UPDATE_CHECK_BOX: 'checkboxes',
    UPDATE_DROP_DOWN: 'dropDownValue',
    UPDATE_PRICE_CALL: 'priceCallData',
    UPDATE_PAGINATION_DATA: 'paginationData',
    SET_STEP_NO: 'stepNo',
    TRACE_ON_CLICK: 'traceOnClick',
    CHECK_FILE_UPLOAD: 'checkFileUpload',
    SET_IS_ALL_CHECKED: 'setIsAllChecked',
    SET_PAGINATION_PAGE: 'setPage',
    CHECK_ACTIVE_LINK: 'checkActiveLink',
    SET_PAGE_ERROR: 'pageError',
    SET_PER_PAGE_COUNT: 'perPageCount',
    UPDATE_ADD_TO_CART_DATA: 'crisprgrnaAddtoCartData',
    SET_COMPANION_PRODUCT_INFORMATION: 'companionProducts',
  }

  if (keys[action.type] && action.type !== 'INIT') {
    const newState = { ...state }
    newState[keys[action.type]] = action.data
    return newState
  } else {
    return initialState
  }
}
